import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, FileText, BookOpen, GraduationCap, Lightbulb, Newspaper, ScrollText, Library, PencilLine, Bookmark, Search, Calendar, Scale, Receipt, ShieldCheck, Sparkles, FileBarChart, TrendingUp, Tags, Calculator, Bell } from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import { SectionHeading } from "@/components/site/SectionHeading";
import { Button } from "@/components/ui/button";
import { fetchKnowledgeHub } from "@/lib/content";
import { SeoFaqBlock } from "@/components/site/SeoFaqBlock";
import { SEO_CONTENT } from "@/components/site/seo-content";
import { ServiceDetailSection } from "@/components/site/ServiceDetailSection";
import { SERVICE_DETAIL_CONTENT } from "@/lib/service-detail-content";
import { breadcrumbScript } from "@/lib/breadcrumb-jsonld";
import { webPageScript } from "@/lib/webpage-jsonld";
import { useWatermarkEnabled } from "@/hooks/use-watermark-settings";
import { jsonLdScript } from "@/lib/jsonld";


const KH_BULLET_ICONS = [
  { Icon: FileBarChart, className: "bg-gradient-to-br from-blue-500 to-purple-600 text-white" },
  { Icon: TrendingUp, className: "bg-pink-500 text-white" },
  { Icon: Tags, className: "bg-indigo-500 text-white" },
  { Icon: Calculator, className: "bg-fuchsia-500 text-white" },
  { Icon: Calendar, className: "bg-gradient-to-br from-orange-500 to-red-500 text-white" },
  { Icon: Bell, className: "bg-teal-500 text-white" },
];

export const Route = createFileRoute("/knowledge-hub/")({
  head: () => ({
    meta: [
      { title: "Income Tax, Tax Return & VAT Knowledge Hub Bangladesh | NBR Guides" },
      { name: "description", content: "Learn how income tax works in Bangladesh — step-by-step guides on income tax return filing, e-Return submission, e-TIN, VAT registration (BIN), Mushak 9.1 monthly VAT return and NBR business tax compliance." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Income Tax & Tax Return Knowledge Hub — Online Tax VAT Bangladesh" },
      { property: "og:description", content: "Practical NBR-aligned guides on income tax, tax return filing, online e-Return, e-TIN, VAT registration (BIN), Mushak 9.1 VAT return and SME compliance in Bangladesh." },
      { property: "og:url", content: "https://onlinetaxvat.com/knowledge-hub" },
    ],
    links: [{ rel: "canonical", href: "https://onlinetaxvat.com/knowledge-hub" }],
    scripts: [
      jsonLdScript({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Knowledge Hub — Online Tax VAT Bangladesh",
          url: "https://onlinetaxvat.com/knowledge-hub",
      }),
      breadcrumbScript([
        { name: "Home", path: "/" },
        { name: "Knowledge Hub", path: "/knowledge-hub" },
      ]),
      webPageScript({
        path: "/knowledge-hub",
        name: "Knowledge Hub — Online Tax VAT Bangladesh",
        description: "Curated knowledge base of NBR-aligned guides, explainers, checklists and updates on VAT registration (BIN), VAT return filing (Mushak 9.1), e-TIN certificate, income tax return, trade license, company registration, ERC/IRC, BIDA, RJSC, fire and food licensing — written for individuals, freelancers, e-commerce sellers and SMEs operating in Bangladesh.",
      }),
    ],
  }),
  component: KnowledgeHubPage,
});

function toKeywords(v: any): string[] {
  if (!v) return [];
  if (Array.isArray(v)) return v.map(String);
  if (typeof v === "string") {
    try { const j = JSON.parse(v); return Array.isArray(j) ? j.map(String) : []; }
    catch { return v.split(",").map(s => s.trim()).filter(Boolean); }
  }
  return [];
}

function KnowledgeHubPage() {
  const { t, i18n } = useTranslation();
  const isBn = (i18n.resolvedLanguage || i18n.language || "en").startsWith("bn");
  const showWatermark = useWatermarkEnabled("knowledge_hub");
  const { data: posts = [], isLoading } = useQuery({ queryKey: ["knowledge_hub"], queryFn: fetchKnowledgeHub });

  return (
    <SiteShell>
      <section className="relative overflow-hidden py-16 md:py-24">
        {showWatermark && (
          <div aria-hidden className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
            {[
              { Icon: BookOpen, cls: "top-[5%] left-[4%] text-emerald-400/25", size: 88, dur: 8, delay: 0, rot: -8 },
              { Icon: GraduationCap, cls: "top-[8%] right-[6%] text-sky-400/25", size: 80, dur: 7.4, delay: 0.4, rot: 6 },
              { Icon: Lightbulb, cls: "top-[28%] left-[10%] text-amber-400/30", size: 70, dur: 6.8, delay: 0.8, rot: -5 },
              { Icon: Newspaper, cls: "top-[32%] right-[12%] text-violet-400/25", size: 76, dur: 7.6, delay: 1.0, rot: 4 },
              { Icon: ScrollText, cls: "top-[52%] left-[6%] text-rose-400/25", size: 74, dur: 7.2, delay: 0.3, rot: 5 },
              { Icon: Library, cls: "top-[56%] right-[8%] text-indigo-400/25", size: 82, dur: 8.2, delay: 0.7, rot: -6 },
              { Icon: PencilLine, cls: "bottom-[18%] left-[12%] text-teal-400/25", size: 68, dur: 7, delay: 1.2, rot: 3 },
              { Icon: Bookmark, cls: "bottom-[12%] right-[14%] text-fuchsia-400/25", size: 70, dur: 7.8, delay: 0.5, rot: -4 },
              { Icon: Search, cls: "bottom-[6%] left-[42%] text-cyan-400/25", size: 66, dur: 6.6, delay: 0.9, rot: 4 },
              { Icon: FileText, cls: "top-[44%] left-[48%] text-emerald-500/20", size: 92, dur: 9, delay: 0.2, rot: 0 },
            ].map(({ Icon, cls, size }, i) => (
              <div
                key={i}
                className={`absolute ${cls} drop-shadow-[0_4px_20px_rgba(0,0,0,0.08)]`}
              >
                <Icon style={{ width: size, height: size }} strokeWidth={1.2} />
              </div>
            ))}

          </div>
        )}
        <div className="container-page relative z-10">
          <h1 className="sr-only">Income Tax &amp; Tax Return Knowledge Hub — Guides, Updates and Resources for Bangladesh Taxpayers</h1>
          <p className="sr-only">Everything you need to understand income tax in Bangladesh: how to prepare a tax return, file the income tax return form online through the NBR e-Return portal, calculate liabilities, claim rebates and complete secure tax payment — updated with the latest Finance Act and NBR circulars.</p>
          <SectionHeading
            eyebrow={t("knowledgeHub.eyebrow")}
            title={<>{t("knowledgeHub.titleA")}<span className="gradient-text">{t("knowledgeHub.titleB")}</span></>}
            description={t("knowledgeHub.desc")}
          />

          {isLoading ? (
            <div
              className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6"
              style={{ contain: "layout", containIntrinsicSize: "1px 720px" }}
              aria-hidden="true"
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl p-5 sm:p-6 bg-[#0f5132] border border-[#0f5132] shadow-[0_10px_30px_-12px_rgba(15,81,50,0.55)] flex flex-col"
                  style={{ minHeight: 340 }}
                />
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div
              className="mt-12"
              style={{ minHeight: 600, contentVisibility: "auto", containIntrinsicSize: "1px 600px" }}
            >
              <p className="text-center text-muted-foreground">{t("knowledgeHub.empty")}</p>
            </div>
          ) : (
            <div
              className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6"
              style={{ contain: "layout", containIntrinsicSize: "1px 720px" }}
            >

              {posts.map((p, i) => {
                const keywords = toKeywords(p.seo_keywords);
                const cat = (p.category || "").toLowerCase();
                const theme = cat.includes("vat") || cat.includes("ভ্যাট")
                  ? { Icon: Receipt, ring: "ring-emerald-400/30", border: "border-[#0f5132]", badge: "bg-[#0a3d24] text-emerald-300 border-emerald-400/30", accent: "text-emerald-300", label: "VAT" }
                  : cat.includes("advisory") || cat.includes("পরামর্শ")
                  ? { Icon: ShieldCheck, ring: "ring-emerald-400/30", border: "border-[#0f5132]", badge: "bg-[#0a3d24] text-emerald-300 border-emerald-400/30", accent: "text-emerald-300", label: "Advisory" }
                  : { Icon: Scale, ring: "ring-emerald-400/30", border: "border-[#0f5132]", badge: "bg-[#0a3d24] text-emerald-300 border-emerald-400/30", accent: "text-emerald-300", label: "Tax Law" };
                const CatIcon = theme.Icon;
                return (
                  <article
                    key={p.id}
                    className={`group relative h-full rounded-2xl p-5 sm:p-6 hover:-translate-y-1 transition-all flex flex-col bg-[#0f5132] border ${theme.border} shadow-[0_10px_30px_-12px_rgba(15,81,50,0.55)] hover:shadow-[0_20px_45px_-15px_rgba(15,81,50,0.75)]`}
                    style={{ minHeight: 340 }}
                  >
                    <div className="flex flex-col flex-1">
                      <div className={`relative flex items-center justify-between`}>
                        <div className={`inline-flex items-center gap-2 rounded-full border ${theme.badge} px-3 py-1 text-[11px] font-semibold uppercase tracking-wider`}>
                          <Sparkles className="h-3 w-3" /> {theme.label}
                        </div>
                        <div className={`h-10 w-10 rounded-xl bg-[#0a3d24] flex items-center justify-center ${theme.accent} ring-1 ring-emerald-400/30`}>
                          <CatIcon className="h-5 w-5" strokeWidth={1.8} />
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2 mb-1">
                        {p.category && (
                          <span className="text-[11px] font-medium text-emerald-100/80 inline-flex items-center gap-1">
                            <span className={`h-1.5 w-1.5 rounded-full bg-emerald-400`} />
                            {p.category}
                          </span>
                        )}
                      </div>
                      <h2 className="font-display font-bold text-lg leading-snug text-white group-hover:text-white transition-colors">
                        <Link to="/knowledge-hub/$slug" params={{ slug: p.slug }}>{p.title}</Link>
                      </h2>
                      {p.meta_description && <p className="mt-2 text-sm text-emerald-100/80 line-clamp-3 flex-1">{p.meta_description}</p>}
                      {keywords.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-1.5">
                          {keywords.slice(0, 3).map((k) => (
                            <span key={k} className="inline-flex items-center rounded-md bg-[#0a3d24]/80 text-emerald-100 px-2 py-0.5 text-[10px] font-mono border border-emerald-400/20">#{k}</span>
                          ))}
                        </div>
                      )}
                      <div className={`mt-5 pt-4 border-t border-emerald-400/25 flex items-center justify-between`}>
                        {p.published_at && (
                          <span className="text-xs text-emerald-200/70 inline-flex items-center gap-1.5">
                            <Calendar className="h-3.5 w-3.5" />
                            {new Date(p.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                          </span>
                        )}
                        <Link to="/knowledge-hub/$slug" params={{ slug: p.slug }} className={`inline-flex items-center gap-1 text-xs font-semibold text-white hover:gap-2 transition-all`}>
                          {t("knowledgeHub.readMore")} <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      </div>
                    </div>
                  </article>

                );
              })}
            </div>
          )}

          <div className="mt-10 text-center">
            <Button asChild size="lg" variant="outline" className="glass-strong">
              <Link to="/blog">{t("knowledgeHub.browseFull")} <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>

          {isBn ? (
            <div
              className="mt-16 max-w-3xl mx-auto text-muted-foreground leading-relaxed"
              style={{ contain: "layout style" }}
            >
              <h1 className="text-3xl font-bold mb-4 text-foreground text-center">নলেজ হাব (Knowledge Hub)</h1>
              <p className="font-medium mb-4 text-center">
                আমাদের নলেজ হাব হলো বাংলাদেশে ট্যাক্স এবং ভ্যাট কমপ্লায়েন্সের জন্য এনবিআর (NBR) নিয়ম অনুযায়ী তৈরি গাইডলাইন, চেকলিস্ট, টেমপ্লেট এবং পলিসির একটি সুবিন্যস্ত লাইব্রেরি।
              </p>
              <p>
                এই লাইব্রেরির প্রতিটি কন্টেন্ট আমাদের সিনিয়র কনসালট্যান্টদের দ্বারা কিউরেট করা এবং বাস্তব কর্মক্ষেত্রের সাথে মিল রেখে সাজানো — যেমন প্রথমবার ই-টিন (e-TIN) আবেদনকারী ফ্রিল্যান্সার, প্রতি মাসে মূসক ৯.১ দাখিল করা লিমিটেড কোম্পানি, আইআরসি (IRC) ডকুমেন্ট প্রস্তুতকারী আমদানিকারক, কিংবা ডিএসসিসি (DSCC) ও ডিএনসিসি (DNCC) থেকে ট্রেড লাইসেন্স নবায়ন করা ক্ষুদ্র ও মাঝারি শিল্প (SME)।
              </p>
              <p className="mt-3">
                এখানে আপনি পাবেন ভ্যাট রেজিস্ট্রেশন (BIN), ভ্যাট রিটার্ন ফাইলিং, বকেয়া ভ্যাট নিষ্পত্তি, ফাস্ট-ট্র্যাক ভ্যাট/বিন রেজিস্ট্রেশন, ব্যক্তিগত ও কোম্পানি ট্যাক্স ফাইলিং, ইআরসি/আইআরসি নিবন্ধন, আরজেএসসি (RJSC) ফাইলিং, বিডা (BIDA) অনুমোদন, ফায়ার, ফুড ও ড্রাগ লাইসেন্স এবং ভ্রমণকারীদের জন্য ভিসা ট্যাক্স ফাইলের চিরসবুজ রেফারেন্স পেজ। প্রতিটি গাইডের সাথে প্রয়োজনীয় ডকুমেন্টের তালিকা, যোগ্যতা, সরকারি ফি, সময়সীমা, সাধারণ বাতিলের কারণ এবং এনবিআর পোর্টালের ধাপে ধাপে স্ক্রিনশট অন্তর্ভুক্ত রয়েছে।
              </p>
              <p className="mt-3">
                আমাদের এই হাব নিয়মিত আপডেট করা হয়: ২০২৬-২০২৭ কর বছরের রিটার্ন সাইকেল, ২০২৬ সালের অনলাইন আয়কর দাখিল, ভ্যাটের সর্বশেষ পরিবর্তন, অডিট প্রস্তুতি, কর পরিকল্পনা এবং ক্রস-বর্ডার ট্যাক্স বিষয়ের নতুন গাইডলাইন এতে নিয়মিত যুক্ত হচ্ছে। বাংলাদেশে ব্যবসা ও ব্যক্তির কর সংক্রান্ত এনবিআর সার্কুলার, এসআরও (SROs) এবং বাজেট আপডেটের সাথে যুক্ত থাকতে এই পেজটি বুকমার্ক করে রাখুন।
              </p>

              <h2 className="text-xl font-semibold mt-6 mb-2 text-foreground">সর্বদা আপডেট থাকুন — বাংলাদেশের ট্যাক্স ও ভ্যাট ইনসাইট</h2>
              <p className="text-sm mb-3">
                অর্থ আইনের মাধ্যমে বাংলাদেশে প্রতি বছরই কর আইন পরিবর্তিত হয়। আমাদের নলেজ হাব সহজ ভাষায় গাইডলাইন ও ডেডলাইন রিমাইন্ডার প্রকাশ করে, যাতে আপনি কোনো কর ছাড় বা রেয়াতের সুযোগ হাতছাড়া না করেন।
              </p>
              <ul className="space-y-3">
                {[
                  { label: "বার্ষিক অর্থ আইনের সারসংক্ষেপ:", text: "প্রতি বছরের বাজেট ও ট্যাক্স আইনের সহজ বাংলা ও ইংরেজি সামারি" },
                  { label: "ট্যাক্স স্ল্যাব আপডেট:", text: "প্রতি জুলাই মাসে অর্থবছর পরিবর্তনের সাথে সাথে নতুন কর হারের আপডেট" },
                  { label: "ভ্যাট হারের পরিবর্তন:", text: "এইচএস কোড (HS Code) এবং সেবার ক্যাটাগরি অনুযায়ী ভ্যাট হারের লাইভ তথ্য" },
                  { label: "বাস্তব কর গণনা পদ্ধতি:", text: "বেতন, ফ্রিল্যান্সিং এবং বাড়ি ভাড়া আয়ের কর গণনার বাস্তব উদাহরণ" },
                  { label: "ডাউনলোডযোগ্য ডেডলাইন ক্যালেন্ডার:", text: "পিডিএফ (PDF) এবং গুগল ক্যালেন্ডার ফরম্যাটে ট্যাক্স ক্যালেন্ডার" },
                  { label: "তাৎক্ষণিক নোটিফিকেশন:", text: "এনবিআর নতুন কোনো এসআরও (SRO) বা সার্কুলার জারি করলেই এলার্ট" },
                ].map((item, i) => {
                  const { Icon, className } = KH_BULLET_ICONS[i];
                  return (
                    <li key={i} className="flex items-start gap-3">
                      <span className={`${className} p-1.5 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 shadow-sm`}>
                        <Icon className="h-4 w-4" strokeWidth={2.25} />
                      </span>
                      <span className="pt-1"><strong className="text-foreground">{item.label}</strong> {item.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <div
              className="mt-16 max-w-3xl mx-auto text-center text-sm leading-relaxed text-muted-foreground space-y-3"
              style={{ contain: "layout style", minHeight: 320 }}
            >
              <p>
                The Knowledge Hub is our structured library of NBR-aligned explainers, checklists, templates and
                policy summaries covering income tax return filing, online e-Return submission and end-to-end VAT
                compliance in Bangladesh. Every entry is curated by senior tax consultants and mapped to a
                real-world workflow — a first-time freelancer applying for an e-TIN certificate, a limited company
                filing monthly Mushak 9.1 VAT return, an importer preparing IRC documentation, or an SME renewing a
                trade license at DSCC or DNCC.
              </p>
              <p>
                You will find clean, evergreen reference pages on VAT registration in Bangladesh (BIN), online VAT
                return filing, VAT arrear settlement, FAST-Track VAT / BIN registration, personal and corporate
                income tax return preparation, company tax filing, ERC / IRC registration, RJSC filings, BIDA
                approvals, fire, food and drug licensing, plus visa tax file packages for travellers. Each guide
                includes required documents, eligibility, government fees, deadlines, common NBR rejection reasons
                and step-by-step screenshots of the NBR e-Return and iVAS portals.
              </p>
              <p>
                The hub is refreshed continuously with new guides for the Income Tax Return 2026-2027 cycle,
                online income tax filing in 2026, latest VAT rate and SRO changes, tax audit preparation, advance
                tax planning, tax rebate calculation and cross-border tax topics. Bookmark this page or browse the
                full blog to stay current with NBR circulars, SROs and Finance Act updates that affect individual
                taxpayers, freelancers, e-commerce sellers and businesses across Bangladesh.
              </p>
            </div>
          )}

          {(() => {
            const c = SEO_CONTENT["knowledge-hub"];
            if (isBn) {
              return (
                <SeoFaqBlock
                  title="সাধারণ জিজ্ঞাসা (FAQ)"
                  faqs={[
                    { q: "আয়কর রিটার্ন দাখিলের শেষ সময় বা ডেডলাইন কবে?", a: "সাধারণত বাংলাদেশে ব্যক্তিগত আয়কর রিটার্ন দাখিলের স্বাভাবিক সময়সীমা প্রতি বছরের ১ জুলাই থেকে ৩০ নভেম্বর পর্যন্ত (ট্যাক্স ডে)। তবে এনবিআর বা সরকারের বিশেষ সিদ্ধান্তের কারণে অনেক সময় এই সময়সীমা পরবর্তীতে বৃদ্ধি করা হতে পারে।" },
                    { q: "ভ্যাট রিটার্ন দাখিলের নির্দিষ্ট তারিখ বা ডিউ ডেট কবে?", a: "প্রতি মাসের ভ্যাট রিটার্ন (মূসক ৯.১) পরবর্তী মাসের ১৫ তারিখের মধ্যে দাখিল করতে হয়। ১৫ তারিখ পার হয়ে গেলে আইন অনুযায়ী জরিমানা ও অতিরিক্ত সুদ আরোপের নিয়ম রয়েছে, তাই সময়মতো সাবমিট করা অত্যন্ত জরুরি।" },
                    { q: "আমি কি আয়কর রেয়াত বা ট্যাক্স রিবেট (Tax Rebate) ক্লেইম করতে পারব?", a: "হ্যাঁ, অবশ্যই! জীবন বীমা, ডিপিএস (DPS), সরকারি সঞ্চয়পত্র বা অনুমোদিত শেয়ার বাজারে নির্দিষ্ট নিয়মে বিনিয়োগ থাকলে আপনি আইন অনুযায়ী ট্যাক্স রিবেট বা কর রেয়াতের সুবিধা পাবেন, যা আপনার চূড়ান্ত করের পরিমাণ অনেক কমিয়ে দেবে।" },
                    { q: "আমি পুরনো বা পূর্ববর্তী বছরের আর্টিকেল ও সার্কুলারগুলো কোথায় পাব?", a: "আমাদের নলেজ হাবের সার্চ অপশন ব্যবহার করে অথবা 'বাংলাদেশ অর্থ আইন (Finance Act)' ক্যাটাগরিতে ব্রাউজ করে আপনি বিগত বছরগুলোর বাজেট সামারি, পুরনো কর হার এবং পূর্ববর্তী সকল এসআরও (SRO) ও সার্কুলার খুব সহজেই খুঁজে পাবেন।" },
                  ]}
                />
              );
            }
            return <SeoFaqBlock title={c.title} intro={c.intro} bullets={c.bullets} bulletIcons={KH_BULLET_ICONS} faqs={c.faqs} />;
          })()}
          <ServiceDetailSection content={SERVICE_DETAIL_CONTENT["knowledge-hub"]} />
        </div>
      </section>
      
    </SiteShell>
  );
}
